import { useEffect, useState, useLayoutEffect, useRef } from "react";
import { DataSheetGrid, textColumn, keyColumn } from "react-datasheet-grid";
import Select from "react-select";
import "react-datasheet-grid/dist/style.css";
import axios from "axios";
import moment from "moment";
import Modal from "./Modal";
import ModalHeader from "./ModalHeader";
import ModalBody from "./ModalBody";
import ModalFooter from "./ModalFooter";

const SelectComponent = ({
  active,
  rowData,
  setRowData,
  focus,
  stopEditing,
  columnData,
}) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (focus) {
      ref.current?.focus();
    } else {
      ref.current?.blur();
    }
  }, [focus]);

  return (
    <Select
      ref={ref}
      styles={{
        container: (provided) => ({
          ...provided,
          flex: 1,
          alignSelf: "stretch",
          pointerEvents: focus ? undefined : "none",
        }),
        control: (provided) => ({
          ...provided,
          height: "100%",
          border: "none",
          boxShadow: "none",
          background: "none",
        }),
        indicatorSeparator: (provided) => ({
          ...provided,
          opacity: 0,
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          opacity: active ? 1 : 0,
        }),
        placeholder: (provided) => ({
          ...provided,
          opacity: active ? 1 : 0,
        }),
      }}
      isDisabled={columnData.disabled}
      value={columnData.choices.find(({ value }) => value === rowData) ?? null}
      menuPortalTarget={document.body}
      menuIsOpen={focus}
      onChange={(option) => {
        setRowData(option.value);
        setTimeout(stopEditing, 0);
      }}
      onMenuClose={() => stopEditing({ nextRow: false })}
      options={columnData.choices}
    />
  );
};

const selectColumn = (options) => ({
  component: SelectComponent,
  columnData: options,
  disableKeys: true,
  keepFocus: true,
  disabled: options.disabled,
  deleteValue: () => null,
  copyValue: ({ rowData }) =>
    options.choices.find((choice) => choice.value === rowData)?.label,
  pasteValue: ({ value }) =>
    options.choices.find((choice) => choice.label === value)?.value ?? null,
});

const gerarColumns = (salas, status, validacoes = []) => {
  const getCellValidationClass = (rowIndex, fieldName, baseClass) => {
    const validacao = validacoes.find((v) => v.id === rowIndex);

    if (!validacao) return baseClass;

    // Se o campo não está definido na validação (undefined), retorna classe base (branco)
    if (validacao[fieldName] === undefined) return baseClass;

    // Se está definido, aplica a classe de acordo com o valor
    if (validacao[fieldName] === true) {
      return `${baseClass} cell-valid`;
    } else {
      return `${baseClass} cell-invalid`;
    }
  };

  return [
    {
      ...keyColumn(
        "hospital",
        selectColumn({
          choices: salas,
        })
      ),
      title: "HOSPITAL",
      cellClassName: ({ rowIndex }) => {
        const baseClass =
          rowIndex === 0
            ? "cell-a1 text-center cell-app"
            : "text-center cell-app";
        return getCellValidationClass(rowIndex, "hospital", baseClass);
      },
      headerClassName: "header-teste text-center",
      disabled: status !== "A",
      minWidth: 200,
    },
    {
      ...keyColumn("inicio", textColumn),
      title: "INÍCIO",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app text-center";
        return getCellValidationClass(rowIndex, "inicio", baseClass);
      },
      disabled: status !== "A",
    },
    {
      ...keyColumn("leito", textColumn),
      title: "LEITO",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app text-center";
        return getCellValidationClass(rowIndex, "leito", baseClass);
      },
      disabled: status !== "A",
    },
    {
      ...keyColumn("obs", textColumn),
      title: "OBS",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app text-center";
        return getCellValidationClass(rowIndex, "obs", baseClass);
      },
      disabled: status !== "A",
    },
    {
      ...keyColumn("nome", textColumn),
      title: "NOME",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app";
        return getCellValidationClass(rowIndex, "nome", baseClass);
      },
      disabled: status !== "A",
      minWidth: 200,
    },
    {
      ...keyColumn("procedimento", textColumn),
      title: "PROCEDIMENTO",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app";
        return getCellValidationClass(rowIndex, "procedimento", baseClass);
      },
      disabled: status !== "A",
      minWidth: 200,
    },
    {
      ...keyColumn("final", textColumn),
      title: "FINAL",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app text-center";
        return getCellValidationClass(rowIndex, "final", baseClass);
      },
      disabled: status !== "A",
    },
    {
      ...keyColumn("convenio", textColumn),
      title: "CONVÊNIO",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app";
        return getCellValidationClass(rowIndex, "convenio", baseClass);
      },
      disabled: status !== "A",
    },
    {
      ...keyColumn("medico", textColumn),
      title: "MÉDICO",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app";
        return getCellValidationClass(rowIndex, "medico", baseClass);
      },
      disabled: status !== "A",
    },
    {
      ...keyColumn("anestesista", textColumn),
      title: "ANESTESISTA",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app text-center";
        return getCellValidationClass(rowIndex, "anestesista", baseClass);
      },
      disabled: status !== "A",
    },
    {
      ...keyColumn("residente", textColumn),
      title: "RESIDENTE",
      headerClassName: "header-teste text-center",
      cellClassName: ({ rowIndex }) => {
        const baseClass = "cell-app text-center";
        return getCellValidationClass(rowIndex, "residente", baseClass);
      },
      disabled: status !== "A",
    },
  ];
};

const App = () => {
  const [id, setId] = useState(null);
  const [api, setApi] = useState("");
  const [data, setData] = useState([]);
  const [validacoes, setValidacoes] = useState([]);
  const [salas, setSalas] = useState([]);
  const [status, setStatus] = useState("A");
  const [columns, setColumns] = useState(
    gerarColumns(salas, status, validacoes)
  );
  const [dataDoc, setDataDoc] = useState(moment().startOf("day").toDate());
  const [random, setRandom] = useState(Math.random());
  const [isLoad, setIsLoad] = useState(false);

  const [salas2, setSalas2] = useState([
    {
      id: "152",
      desc: "CONSULTORIO GECOM",
      status: "A",
      label: "CONSULTORIO GECOM",
      value: "152",
    },
    {
      id: "153",
      desc: "HSP - SALA 1",
      status: "A",
      label: "HSP - SALA 1",
      value: "153",
    },
    {
      id: "154",
      desc: "HSP - SALA 2",
      status: "I",
      label: "HSP - SALA 2",
      value: "154",
    },
    {
      id: "155",
      desc: "HSP - SALA 3",
      status: "I",
      label: "HSP - SALA 3",
      value: "155",
    },
    {
      id: "13",
      desc: "DR. ANTHONY",
      status: "A",
      label: "DR. ANTHONY",
      value: "13",
    },
    {
      id: "7",
      desc: "RC - TOMOGRAFIA",
      status: "A",
      label: "RC - TOMOGRAFIA",
      value: "7",
    },
    {
      id: "167",
      desc: "HIM - SALA 1",
      status: "A",
      label: "HIM - SALA 1",
      value: "167",
    },
    {
      id: "11",
      desc: "DR HUMBERTO - EXAMES",
      status: "A",
      label: "DR HUMBERTO - EXAMES",
      value: "11",
    },
    {
      id: "156",
      desc: "HMD - SALA 3",
      status: "A",
      label: "HMD - SALA 3",
      value: "156",
    },
    {
      id: "157",
      desc: "HMD - SALA 4",
      status: "A",
      label: "HMD - SALA 4",
      value: "157",
    },
    {
      id: "158",
      desc: "HMD - SALA 5",
      status: "A",
      label: "HMD - SALA 5",
      value: "158",
    },
    {
      id: "159",
      desc: "HMV - SALA 5",
      status: "A",
      label: "HMV - SALA 5",
      value: "159",
    },
    {
      id: "160",
      desc: "HMV/HEMO - SALA 2",
      status: "A",
      label: "HMV/HEMO - SALA 2",
      value: "160",
    },
    {
      id: "161",
      desc: "HSC - SALA 9",
      status: "A",
      label: "HSC - SALA 9",
      value: "161",
    },
    {
      id: "162",
      desc: "HSC - SALA 10",
      status: "A",
      label: "HSC - SALA 10",
      value: "162",
    },
    {
      id: "163",
      desc: "ICFUC - SALA 2",
      status: "A",
      label: "ICFUC - SALA 2",
      value: "163",
    },
    {
      id: "164",
      desc: "ICFUC - SALA 3",
      status: "A",
      label: "ICFUC - SALA 3",
      value: "164",
    },
    {
      id: "165",
      desc: "ICFUC - SALA 4",
      status: "A",
      label: "ICFUC - SALA 4",
      value: "165",
    },
    {
      id: "166",
      desc: "NORA TEIXEIRA",
      status: "A",
      label: "NORA TEIXEIRA",
      value: "166",
    },
    {
      id: "169",
      desc: "PPF - SALA 4",
      status: "A",
      label: "PPF - SALA 4",
      value: "169",
    },
    {
      id: "171",
      desc: "HPUC- AVULSO",
      status: "A",
      label: "HPUC- AVULSO",
      value: "171",
    },
    {
      id: "121",
      desc: "HED/Cinecors",
      status: "A",
      label: "HED/Cinecors",
      value: "121",
    },
    {
      id: "168",
      desc: "HDC - SALA 1",
      status: "A",
      label: "HDC - SALA 1",
      value: "168",
    },
    {
      id: "3",
      desc: "RC - RAIO X",
      status: "A",
      label: "RC - RAIO X",
      value: "3",
    },
    {
      id: "151",
      desc: "LIFE PLUS - SALA 1",
      status: "A",
      label: "LIFE PLUS - SALA 1",
      value: "151",
    },
    {
      id: "50",
      desc: "HED/EDA - SALA 1",
      status: "A",
      label: "HED/EDA - SALA 1",
      value: "50",
    },
    {
      id: "51",
      desc: "HED/EDA - SALA 2",
      status: "A",
      label: "HED/EDA - SALA 2",
      value: "51",
    },
    {
      id: "52",
      desc: "HED/EDA - SALA 3",
      status: "A",
      label: "HED/EDA - SALA 3",
      value: "52",
    },
    {
      id: "53",
      desc: "HED/EDA - SALA 4",
      status: "A",
      label: "HED/EDA - SALA 4",
      value: "53",
    },
    {
      id: "54",
      desc: "ICFUC/EEF",
      status: "A",
      label: "ICFUC/EEF",
      value: "54",
    },
    {
      id: "55",
      desc: "HDP - SALA 1",
      status: "A",
      label: "HDP - SALA 1",
      value: "55",
    },
    {
      id: "56",
      desc: "HDP - SALA 2",
      status: "A",
      label: "HDP - SALA 2",
      value: "56",
    },
    {
      id: "57",
      desc: "HDP/HEMO",
      status: "A",
      label: "HDP/HEMO",
      value: "57",
    },
    {
      id: "58",
      desc: "HDVS/CCA - SALA 8",
      status: "A",
      label: "HDVS/CCA - SALA 8",
      value: "58",
    },
    {
      id: "59",
      desc: "HDVS/CCA - SALA 1",
      status: "A",
      label: "HDVS/CCA - SALA 1",
      value: "59",
    },
    {
      id: "60",
      desc: "HDVS/CCA - SALA 2",
      status: "A",
      label: "HDVS/CCA - SALA 2",
      value: "60",
    },
    {
      id: "61",
      desc: "HDVS/CCA - SALA 3",
      status: "A",
      label: "HDVS/CCA - SALA 3",
      value: "61",
    },
    {
      id: "62",
      desc: "HDVS/CCA - SALA 5",
      status: "A",
      label: "HDVS/CCA - SALA 5",
      value: "62",
    },
    {
      id: "63",
      desc: "HDVS/CCA - SALA 6",
      status: "A",
      label: "HDVS/CCA - SALA 6",
      value: "63",
    },
    {
      id: "64",
      desc: "HDVS/CCA - SALA 7",
      status: "A",
      label: "HDVS/CCA - SALA 7",
      value: "64",
    },
    {
      id: "66",
      desc: "HDVS/CCTX - SALA 3",
      status: "A",
      label: "HDVS/CCTX - SALA 3",
      value: "66",
    },
    {
      id: "67",
      desc: "HDVS/CCTX - SALA 5",
      status: "A",
      label: "HDVS/CCTX - SALA 5",
      value: "67",
    },
    {
      id: "65",
      desc: "HDVS/CCTX - SALA 1",
      status: "A",
      label: "HDVS/CCTX - SALA 1",
      value: "65",
    },
    {
      id: "68",
      desc: "HDVS/CCTX - SALA 6",
      status: "A",
      label: "HDVS/CCTX - SALA 6",
      value: "68",
    },
    {
      id: "69",
      desc: "STA RITA - SALA 1",
      status: "A",
      label: "STA RITA - SALA 1",
      value: "69",
    },
    {
      id: "70",
      desc: "HSC - SALA 5",
      status: "A",
      label: "HSC - SALA 5",
      value: "70",
    },
    {
      id: "71",
      desc: "HSC - SALA 4",
      status: "A",
      label: "HSC - SALA 4",
      value: "71",
    },
    {
      id: "72",
      desc: "STA  ANA - SALA 1",
      status: "A",
      label: "STA  ANA - SALA 1",
      value: "72",
    },
    {
      id: "73",
      desc: "PPF - SALA 4",
      status: "I",
      label: "PPF - SALA 4",
      value: "73",
    },
    {
      id: "74",
      desc: "PPF - SALA 3",
      status: "A",
      label: "PPF - SALA 3",
      value: "74",
    },
    {
      id: "75",
      desc: "PPF - SALA 2",
      status: "A",
      label: "PPF - SALA 2",
      value: "75",
    },
    {
      id: "76",
      desc: "PPF - SALA 1",
      status: "A",
      label: "PPF - SALA 1",
      value: "76",
    },
    {
      id: "78",
      desc: "HMD - SALA 2",
      status: "A",
      label: "HMD - SALA 2",
      value: "78",
    },
    {
      id: "79",
      desc: "HMD/HIBRIDA",
      status: "A",
      label: "HMD/HIBRIDA",
      value: "79",
    },
    {
      id: "80",
      desc: "HMD - SALA 1",
      status: "A",
      label: "HMD - SALA 1",
      value: "80",
    },
    {
      id: "77",
      desc: "ICFUC/HEMO",
      status: "A",
      label: "ICFUC/HEMO",
      value: "77",
    },
    {
      id: "81",
      desc: "HMD/CENTER",
      status: "A",
      label: "HMD/CENTER",
      value: "81",
    },
    {
      id: "83",
      desc: "HMD/HEMO",
      status: "A",
      label: "HMD/HEMO",
      value: "83",
    },
    {
      id: "82",
      desc: "HMD/EDA",
      status: "A",
      label: "HMD/EDA",
      value: "82",
    },
    {
      id: "84",
      desc: "HMV - SALA 1",
      status: "A",
      label: "HMV - SALA 1",
      value: "84",
    },
    {
      id: "85",
      desc: "HMV - SALA 2",
      status: "A",
      label: "HMV - SALA 2",
      value: "85",
    },
    {
      id: "86",
      desc: "HMV - SALA 3",
      status: "A",
      label: "HMV - SALA 3",
      value: "86",
    },
    {
      id: "88",
      desc: "HMV/EDA",
      status: "A",
      label: "HMV/EDA",
      value: "88",
    },
    {
      id: "89",
      desc: "HMV/HEMO - SALA 1",
      status: "A",
      label: "HMV/HEMO - SALA 1",
      value: "89",
    },
    {
      id: "87",
      desc: "HMV - SALA 4",
      status: "A",
      label: "HMV - SALA 4",
      value: "87",
    },
    {
      id: "90",
      desc: "HPA - SALA 1",
      status: "A",
      label: "HPA - SALA 1",
      value: "90",
    },
    {
      id: "92",
      desc: "HSC - SALA 2",
      status: "A",
      label: "HSC - SALA 2",
      value: "92",
    },
    {
      id: "93",
      desc: "HSC - SALA 3",
      status: "A",
      label: "HSC - SALA 3",
      value: "93",
    },
    {
      id: "96",
      desc: "HSF - SALA 2",
      status: "A",
      label: "HSF - SALA 2",
      value: "96",
    },
    {
      id: "94",
      desc: "HSF - SALA 1",
      status: "A",
      label: "HSF - SALA 1",
      value: "94",
    },
    {
      id: "97",
      desc: "HSF - SALA 3",
      status: "A",
      label: "HSF - SALA 3",
      value: "97",
    },
    {
      id: "98",
      desc: "HSF - SALA 4",
      status: "A",
      label: "HSF - SALA 4",
      value: "98",
    },
    {
      id: "99",
      desc: "HSF - SALA 5",
      status: "A",
      label: "HSF - SALA 5",
      value: "99",
    },
    {
      id: "100",
      desc: "HSJ - SALA 4",
      status: "A",
      label: "HSJ - SALA 4",
      value: "100",
    },
    {
      id: "101",
      desc: "HSJ - SALA 5",
      status: "A",
      label: "HSJ - SALA 5",
      value: "101",
    },
    {
      id: "91",
      desc: "HSC - SALA 1",
      status: "A",
      label: "HSC - SALA 1",
      value: "91",
    },
    {
      id: "103",
      desc: "HSJ - SALA 1",
      status: "A",
      label: "HSJ - SALA 1",
      value: "103",
    },
    {
      id: "104",
      desc: "NEURO/HSJ",
      status: "A",
      label: "NEURO/HSJ",
      value: "104",
    },
    {
      id: "105",
      desc: "ICFUC - SALA 1",
      status: "A",
      label: "ICFUC - SALA 1",
      value: "105",
    },
    {
      id: "95",
      desc: "HSC - SALA LITO",
      status: "A",
      label: "HSC - SALA LITO",
      value: "95",
    },
    {
      id: "106",
      desc: "ICFUC/CC2",
      status: "A",
      label: "ICFUC/CC2",
      value: "106",
    },
    {
      id: "107",
      desc: "STO ANTONIO - SALA 1",
      status: "A",
      label: "STO ANTONIO - SALA 1",
      value: "107",
    },
    {
      id: "108",
      desc: "HED - 6 A",
      status: "A",
      label: "HED - 6 A",
      value: "108",
    },
    {
      id: "109",
      desc: "HED - 6 D",
      status: "A",
      label: "HED - 6 D",
      value: "109",
    },
    {
      id: "110",
      desc: "HED - 6 C",
      status: "A",
      label: "HED - 6 C",
      value: "110",
    },
    {
      id: "111",
      desc: "HED - 6 B",
      status: "A",
      label: "HED - 6 B",
      value: "111",
    },
    {
      id: "112",
      desc: "HED - 8 D",
      status: "A",
      label: "HED - 8 D",
      value: "112",
    },
    {
      id: "113",
      desc: "HED - 7 C",
      status: "A",
      label: "HED - 7 C",
      value: "113",
    },
    {
      id: "114",
      desc: "HED - 7 B",
      status: "A",
      label: "HED - 7 B",
      value: "114",
    },
    {
      id: "115",
      desc: "HED - 7 A",
      status: "A",
      label: "HED - 7 A",
      value: "115",
    },
    {
      id: "116",
      desc: "HED - 8 C",
      status: "A",
      label: "HED - 8 C",
      value: "116",
    },
    {
      id: "117",
      desc: "HED - 7 D",
      status: "A",
      label: "HED - 7 D",
      value: "117",
    },
    {
      id: "118",
      desc: "HED - 8 A",
      status: "A",
      label: "HED - 8 A",
      value: "118",
    },
    {
      id: "119",
      desc: "HED - 8 B",
      status: "A",
      label: "HED - 8 B",
      value: "119",
    },
    {
      id: "120",
      desc: "CONSULTORIO - HED",
      status: "A",
      label: "CONSULTORIO - HED",
      value: "120",
    },
    {
      id: "102",
      desc: "HDP/EDA",
      status: "A",
      label: "HDP/EDA",
      value: "102",
    },
    {
      id: "26",
      desc: "Raio-X Teste",
      status: "A",
      label: "Raio-X Teste",
      value: "26",
    },
    {
      id: "32",
      desc: "Raio-X 2",
      status: "A",
      label: "Raio-X 2",
      value: "32",
    },
    {
      id: "4",
      desc: "RC - RESSONÂNCIA",
      status: "A",
      label: "RC - RESSONÂNCIA",
      value: "4",
    },
    {
      id: "124",
      desc: "HED/EDA - FAT AVULSO",
      status: "A",
      label: "HED/EDA - FAT AVULSO",
      value: "124",
    },
    {
      id: "123",
      desc: "HED - FAT AVULSO",
      status: "A",
      label: "HED - FAT AVULSO",
      value: "123",
    },
    {
      id: "125",
      desc: "ICFUC - FAT AVULSO",
      status: "A",
      label: "ICFUC - FAT AVULSO",
      value: "125",
    },
    {
      id: "126",
      desc: "HDP - FAT AVULSO",
      status: "A",
      label: "HDP - FAT AVULSO",
      value: "126",
    },
    {
      id: "127",
      desc: "HDVS - FAT AVULSO",
      status: "A",
      label: "HDVS - FAT AVULSO",
      value: "127",
    },
    {
      id: "129",
      desc: "HMV - FAT AVULSO",
      status: "A",
      label: "HMV - FAT AVULSO",
      value: "129",
    },
    {
      id: "128",
      desc: "HMD - FAT AVULSO",
      status: "A",
      label: "HMD - FAT AVULSO",
      value: "128",
    },
    {
      id: "131",
      desc: "HSF - FAT AVULSO",
      status: "A",
      label: "HSF - FAT AVULSO",
      value: "131",
    },
    {
      id: "132",
      desc: "HED/Cinecors - FAT AVULSO",
      status: "A",
      label: "HED/Cinecors - FAT AVULSO",
      value: "132",
    },
    {
      id: "134",
      desc: "STA RITA - FAT AVULSO",
      status: "A",
      label: "STA RITA - FAT AVULSO",
      value: "134",
    },
    {
      id: "133",
      desc: "STO ANTONIO - FAT AVULSO",
      status: "A",
      label: "STO ANTONIO - FAT AVULSO",
      value: "133",
    },
    {
      id: "136",
      desc: "PPF  - FAT AVULSO",
      status: "A",
      label: "PPF  - FAT AVULSO",
      value: "136",
    },
    {
      id: "135",
      desc: "STA  ANA  - FAT AVULSO",
      status: "A",
      label: "STA  ANA  - FAT AVULSO",
      value: "135",
    },
    {
      id: "130",
      desc: "HSC - FAT AVULSO",
      status: "A",
      label: "HSC - FAT AVULSO",
      value: "130",
    },
    {
      id: "137",
      desc: "HSJ - FAT AVULSO",
      status: "A",
      label: "HSJ - FAT AVULSO",
      value: "137",
    },
    {
      id: "138",
      desc: "BLANC",
      status: "A",
      label: "BLANC",
      value: "138",
    },
    {
      id: "139",
      desc: "BLANC - FAT AVULSO",
      status: "A",
      label: "BLANC - FAT AVULSO",
      value: "139",
    },
    {
      id: "146",
      desc: "PPF - EDA",
      status: "A",
      label: "PPF - EDA",
      value: "146",
    },
    {
      id: "143",
      desc: "HDVS/CCA - SALA 4",
      status: "A",
      label: "HDVS/CCA - SALA 4",
      value: "143",
    },
    {
      id: "144",
      desc: "HDVS/CCTX - SALA 2",
      status: "A",
      label: "HDVS/CCTX - SALA 2",
      value: "144",
    },
    {
      id: "145",
      desc: "HDVS/CCTX - SALA 4",
      status: "A",
      label: "HDVS/CCTX - SALA 4",
      value: "145",
    },
    {
      id: "147",
      desc: "HDVS/CCTX - SALA 7",
      status: "A",
      label: "HDVS/CCTX - SALA 7",
      value: "147",
    },
    {
      id: "148",
      desc: "HDVS/CCTX - SALA 8",
      status: "A",
      label: "HDVS/CCTX - SALA 8",
      value: "148",
    },
    {
      id: "140",
      desc: "HSC - SALA 6",
      status: "A",
      label: "HSC - SALA 6",
      value: "140",
    },
    {
      id: "141",
      desc: "HSC - SALA 7",
      status: "A",
      label: "HSC - SALA 7",
      value: "141",
    },
    {
      id: "142",
      desc: "HSC - SALA 8",
      status: "A",
      label: "HSC - SALA 8",
      value: "142",
    },
    {
      id: "149",
      desc: "HUMANIZA - SALA 1",
      status: "A",
      label: "HUMANIZA - SALA 1",
      value: "149",
    },
    {
      id: "150",
      desc: "HPUC - SALA 1",
      status: "A",
      label: "HPUC - SALA 1",
      value: "150",
    },
    {
      id: "17",
      desc: "Agenda braba",
      status: "A",
      label: "Agenda braba",
      value: "17",
    },
    {
      id: "170",
      desc: "HDJB - SALA 1",
      status: "A",
      label: "HDJB - SALA 1",
      value: "170",
    },
    {
      id: "31",
      desc: "Raio-X 1",
      status: "A",
      label: "Raio-X 1",
      value: "31",
    },
  ]);

  const [searchTerm, setSearchTerm] = useState("");

  const [tipoBusca, setTipoBusca] = useState("");
  const [tipoBuscaDesc, setTipoBuscaDesc] = useState("");

  // Refs para guardar os valores atuais
  const dataRef = useRef(data);
  const columnsRef = useRef(columns);

  const gridRef = useRef();

  const [modalBusca, setModalBusca] = useState(false);

  const [results, setResults] = useState([]);

  const [linhaSelecionada, setLinhaSelecionada] = useState(null);

  // Sempre que data ou columns mudam, atualiza as refs
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  const selecionarItemBusca = (item) => {
    let localData = [...data];
    localData[linhaSelecionada][tipoBusca] =
      tipoBusca === "anestesista" || tipoBusca === "residente"
        ? item.sigla
        : item.desc;

    let localValidacoes = [...validacoes];
    for (let item of localValidacoes) {
      if (item.id === linhaSelecionada) {
        item[tipoBusca] = true;
      }
    }

    setData(localData);
    setModalBusca(false);
    setValidacoes(localValidacoes);
  };

  // Event listener criado UMA VEZ APENAS
  useEffect(() => {
    const listaTiposBusca = [
      "procedimento",
      "convenio",
      "medico",
      "anestesista",
      "residente",
    ];

    const handleKeyDown = (e) => {
      if (e.altKey && e.key === "j") {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Acessa SEMPRE os valores mais recentes
        console.log("Data atual:", dataRef.current);
        console.log("Columns atuais:", columnsRef.current);

        const activeCell = gridRef.current?.activeCell;
        if (activeCell) {
          let localTipo = activeCell.colId;

          if (listaTiposBusca.includes(localTipo)) {
            setModalBusca(true);
            setTipoBusca(localTipo);
            setLinhaSelecionada(activeCell.row);
            setResults([]);
            setSearchTerm("");

            if (localTipo === "procedimento") {
              setTipoBuscaDesc("PROCEDIMENTO");
            } else if (localTipo === "convenio") {
              setTipoBuscaDesc("CONVÊNIO");
            } else if (localTipo === "medico") {
              setTipoBuscaDesc("MÉDICO");
            } else if (localTipo === "anestesista") {
              setTipoBuscaDesc("ANESTESISTA");
            } else if (localTipo === "residente") {
              setTipoBuscaDesc("RESIDENTE");
            }
          }

          console.log(activeCell);
          console.log(
            `Active cell: Column ${activeCell.col}, Row ${activeCell.row}`
          );
        } else {
          console.log("No cell is active.");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []); // ✅ Array vazio - listener criado só uma vez

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let localApi = params.get("api");
    let localId = params.get("id");
    setApi(localApi);
    setId(localId);
    buscarSalas(localApi);
  }, []);

  useEffect(() => {
    if (salas.length > 0) {
      buscarDoc(id, api);
      setRandom(Math.random());
    }
  }, [salas]);

  useEffect(() => {
    if (validacoes.length > 0) {
      setTimeout(() => {
        const novasColumns = gerarColumns(salas, status, validacoes);
        setColumns([...novasColumns]);
        setRandom(Math.random());
      }, 1000);
    }
  }, [validacoes]);

  const buscarDoc = (id, api) => {
    if (id === null) return false;

    axios
      .get(`${api}/api/buscar-doc/${id}`, {})
      .then((response) => {
        let doc = response.data.doc;

        if (doc === null) {
          notificar("warning", "Não foi possível encontrar!");
          return false;
        }

        setStatus(doc.status);
        setDataDoc(doc.dtDoc);

        let localData = [];

        if (doc.linhas.length > 0) {
          for (let item of doc.linhas) {
            localData.push(item);
          }
          setData(localData);
        } else {
          setData((prevData) => {
            const initialData = [];
            for (let i = 0; i < 500; i++) {
              initialData.push(getDefaultValue());
            }
            return initialData;
          });
        }

        setColumns(gerarColumns(salas, doc.status, validacoes));
        setIsLoad(true);
        // const input = document.querySelector(".dsg-input");
        // if (input) {
        //   input.click();
        // }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getDefaultValue = () => {
    return {
      hospital: null,
      inicio: "",
      leito: "",
      obs: "",
      nome: "",
      procedimento: "",
      final: "",
      convenio: "",
      medico: "",
      anestesista: "",
      residente: "",
    };
  };

  const salvarDoc = () => {
    let docSalvar = {
      linhas: data,
    };

    if (id !== null) {
      docSalvar._id = id;
    }

    axios
      .post(`${api}/api/salvar-doc/`, { doc: docSalvar }, {})
      .then((response) => {
        notificar("success", "Documento salvo com sucesso!");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const gerarAgendamentos = () => {
    let localDoc = {
      _id: id,
      linhas: data,
    };

    axios
      .post(`${api}/api/gerar-agendamentos/`, { doc: localDoc }, {})
      .then((response) => {
        notificar("success", "Agendamentos importados com sucesso!");
        setTimeout(() => {
          redirect("/lista-doc-importacao");
        }, 1500);
        // window.location.reload();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const validarAgendamentos = () => {
    let localDoc = {
      _id: id,
      linhas: data,
    };

    axios
      .post(`${api}/api/validar-agendamentos/`, { doc: localDoc }, {})
      .then((response) => {
        console.log("Agendamentos validados com sucesso!");

        console.log(response.data.validacoes);

        notificar("success", "Agendamentos validados com sucesso!");

        setValidacoes(response.data.validacoes);

        // window.location.reload();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const buscarSalas = async (api) => {
    await axios
      .get(`${api}/api/buscar-salas`, {})
      .then((response) => {
        let salas = response.data.salas;

        salas = salas.sort((a, b) => a.desc.localeCompare(b.desc));

        salas = salas.map((it) => {
          return {
            ...it,
            value: it.id,
            label: it.desc,
          };
        });
        setSalas(salas);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleSearch = async () => {
    if (searchTerm === "") {
      notificar("warning", "Favor informar uma pesquisa!");
      // alert("Favor informar uma pesquisa!");
      return false;
    }

    if (tipoBusca !== "anestesista" && tipoBusca !== "residente") {
      if (searchTerm.length < 3) {
        notificar(
          "warning",
          "Digite pelo menos 3 caracteres para fazer a busca..."
        );
        return false;
      }
    }

    await axios
      .post(`${api}/api/buscar`, { type: tipoBusca, pesquisa: searchTerm }, {})
      .then((response) => {
        let lista = response.data.lista;
        console.clear();
        console.log(lista);
        setResults(lista);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const chamarFuncaoDoPai = (action, dados) => {
    window.parent.postMessage(
      {
        action,
        payload: dados,
      },
      "https://sane.estsistemas.com.br" //"http://localhost:3401"
    );
  };

  const notificar = (type, message) => {
    chamarFuncaoDoPai("notificacao", { type, message });
  };

  const redirect = (url) => {
    chamarFuncaoDoPai("redirect", { url });
  };

  return (
    <div style={{ height: "100%", overflow: "hidden" }}>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#d6d6c9",
          padding: "5px",
          fontSize: "14px",
        }}
      >
        Documento de Importação {moment(dataDoc).format("DD/MM/YYYY")} -{" "}
        <span style={{ fontWeight: "bold" }}>
          &nbsp;
          {status === "A"
            ? "PENDENTE"
            : status === "B"
            ? "IMPORTADO"
            : "CANCELADO"}
        </span>{" "}
      </div>

      {salas.length > 0 && isLoad && (
        <DataSheetGrid
          key={random}
          ref={gridRef}
          height={500}
          value={data}
          onChange={(value) => {

            console.clear()
            console.log('@@@@@')


            console.log()

            if (status === "A") {
              setData(value);
              console.log(value);
            }
          }}
          columns={columns}
        />
      )}

      {status === "A" && (
        <div
          style={{
            marginTop: "10px",
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            marginBottom: "200px",
          }}
        >
          <div style={{ margin: "5px" }}>
            <button
              className="btn btn-secondary"
              onClick={() => redirect("/lista-doc-importacao")}
            >
              VOLTAR
            </button>
          </div>
          <div style={{ margin: "5px" }}>
            <button className="btn btn-primary" onClick={salvarDoc}>
              SALVAR
            </button>
          </div>
          <div style={{ margin: "5px" }}>
            <button
              className="btn btn-warning"
              style={{ color: "white" }}
              onClick={validarAgendamentos}
            >
              VALIDAR DADOS
            </button>
          </div>
          <div style={{ margin: "5px" }}>
            <button
              className="btn btn-success"
              onClick={() => {
                if (
                  confirm("Tem certeza que deseja importar estes agendamentos?")
                ) {
                  gerarAgendamentos();
                } else {
                  console.log("Cancelado!");
                }
              }}
            >
              IMPORTAR AGENDAMENTOS
            </button>
          </div>
        </div>
      )}

      <Modal isOpen={modalBusca} onClose={() => setModalBusca(false)}>
        <ModalHeader onClose={() => setModalBusca(false)}>
          BUSCA: {tipoBuscaDesc}
        </ModalHeader>

        <ModalBody>
          <div style={{ marginBottom: "20px" }}>
            <label
              htmlFor="busca"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: 500,
                fontSize: "12px"
              }}
            >
              Pesquisa
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                id="busca"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              <button
                className="btn btn-info"
                onClick={handleSearch}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#0891b2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                🔍
              </button>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize:"12px" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    ID
                  </th>
                  {(tipoBusca === "anestesista" ||
                    tipoBusca === "residente") && (
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      SIGLA
                    </th>
                  )}
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    DESCRIÇÃO
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.map((item) => (
                  <tr
                    key={item.id}
                    style={{ borderBottom: "1px solid #f3f4f6" }}
                  >
                    <td
                      onClick={() => selecionarItemBusca(item)}
                      style={{ padding: "12px", cursor: "pointer" }}
                    >
                      {item.id}
                    </td>
                    {(tipoBusca === "anestesista" ||
                      tipoBusca === "residente") && (
                      <td
                        onClick={() => selecionarItemBusca(item)}
                        style={{ padding: "12px", cursor: "pointer" }}
                      >
                        {item.sigla}
                      </td>
                    )}
                    <td
                      onClick={() => selecionarItemBusca(item)}
                      style={{ padding: "12px", cursor: "pointer" }}
                    >
                      {item.desc}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {results.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#9ca3af",
                  fontSize: "12px"
                }}
              >
                Nenhum resultado encontrado
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            className="btn btn-secondary"
            onClick={() => setModalBusca(false)}
          >
            FECHAR
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default App;
